import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { LogoFull } from "./Logo";
import { Calendar, Tag, ArrowLeft, User } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    axios.get(`${API}/blog/posts/${slug}`).then(r => {
      setPost(r.data);
      // Set page title for SEO
      document.title = `${r.data.title} | VeroBill Blog`;
    }).catch(() => setNotFound(true)).finally(() => setLoading(false));
  }, [slug]);

  const formatDate = (iso) => {
    if (!iso) return "";
    return new Date(iso).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center">
        <p className="text-[#9CA3AF]">Loading...</p>
      </div>
    );
  }

  if (notFound || !post) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex flex-col items-center justify-center gap-4">
        <p className="text-2xl font-bold text-[#0A0A0A]">Post not found</p>
        <Link to="/blog" className="text-sm text-[#6B7280] hover:text-[#0A0A0A] flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> Back to blog
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Header */}
      <header className="border-b border-[#E5E7EB] bg-white sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/" data-testid="blogpost-logo"><LogoFull size={24} /></Link>
          <nav className="flex items-center gap-6">
            <Link to="/blog" className="text-[13px] text-[#6B7280] hover:text-[#0A0A0A]" data-testid="blogpost-back">Blog</Link>
            <Link to="/login" className="text-[13px] font-medium text-[#0A0A0A] border border-[#0A0A0A] px-4 py-1.5 hover:bg-[#0A0A0A] hover:text-white transition-colors">Log in</Link>
          </nav>
        </div>
      </header>

      <article className="max-w-3xl mx-auto px-6 py-12" data-testid="blog-post-content">
        {/* Back link */}
        <Link to="/blog" className="inline-flex items-center gap-1 text-[12px] text-[#9CA3AF] hover:text-[#0A0A0A] uppercase tracking-wider font-semibold mb-8">
          <ArrowLeft className="w-3.5 h-3.5" /> All articles
        </Link>

        {/* Meta */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <span className="flex items-center gap-1 text-[12px] text-[#9CA3AF] font-mono">
            <Calendar className="w-3.5 h-3.5" />
            {formatDate(post.published_at || post.created_at)}
          </span>
          <span className="flex items-center gap-1 text-[12px] text-[#9CA3AF] font-mono">
            <User className="w-3.5 h-3.5" />
            {post.author || "VeroBill Team"}
          </span>
        </div>

        {/* Title */}
        <h1 className="text-3xl sm:text-4xl lg:text-[42px] font-black text-[#0A0A0A] tracking-tight leading-[1.1] mb-6">
          {post.title}
        </h1>

        {/* Tags */}
        {post.tags?.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-8">
            {post.tags.map(tag => (
              <span key={tag} className="flex items-center gap-1 text-[11px] bg-[#F3F4F6] text-[#6B7280] px-2.5 py-1 font-mono">
                <Tag className="w-2.5 h-2.5" />{tag}
              </span>
            ))}
          </div>
        )}

        {/* Hero Image */}
        {post.thumbnail_base64 && (
          <div className="mb-10 border border-[#E5E7EB]">
            <img
              src={`data:image/png;base64,${post.thumbnail_base64}`}
              alt={post.title}
              className="w-full"
            />
          </div>
        )}

        {/* Content */}
        <div
          className="prose prose-lg max-w-none
            prose-headings:font-bold prose-headings:text-[#0A0A0A] prose-headings:tracking-tight
            prose-h2:text-xl prose-h2:mt-10 prose-h2:mb-4
            prose-h3:text-lg prose-h3:mt-8 prose-h3:mb-3
            prose-p:text-[#374151] prose-p:leading-[1.8] prose-p:text-base
            prose-li:text-[#374151] prose-li:leading-[1.7]
            prose-strong:text-[#0A0A0A]
            prose-a:text-[#0A0A0A] prose-a:underline prose-a:underline-offset-2"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />

        {/* CTA */}
        <div className="mt-16 border-t border-[#E5E7EB] pt-10">
          <div className="bg-[#0A0A0A] text-white p-8">
            <h3 className="text-xl font-bold mb-2">Ready to send professional invoices?</h3>
            <p className="text-[#9CA3AF] text-sm mb-4">VeroBill lets you create and send invoices in under 30 seconds. Free to start.</p>
            <Link
              to="/register"
              className="inline-block bg-white text-[#0A0A0A] font-semibold text-sm px-6 py-2.5 hover:bg-[#F3F4F6] transition-colors"
              data-testid="blogpost-cta"
            >
              Create your first invoice free
            </Link>
          </div>
        </div>
      </article>

      {/* Footer */}
      <footer className="border-t border-[#E5E7EB] py-8 px-6 bg-white mt-10">
        <div className="max-w-3xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <LogoFull size={20} />
          <div className="flex items-center gap-6">
            <Link to="/" className="text-[13px] text-[#6B7280] hover:text-[#0A0A0A]">Home</Link>
            <Link to="/blog" className="text-[13px] text-[#6B7280] hover:text-[#0A0A0A]">Blog</Link>
            <Link to="/register" className="text-[13px] text-[#6B7280] hover:text-[#0A0A0A]">Sign up free</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
